import React from 'react'
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import Slide from "@material-ui/core/Slide";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import CloseIcon from "@material-ui/icons/Close";
import Button from "../CustomButtons/Button.jsx";
import MaterialIcon from "@mdi/react";
import { mdiFileDocumentOutline, mdiEyeOutline, mdiDownload, mdiUpload, mdiCog, mdiCrown } from '@mdi/js';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="down" ref={ref} {...props} />;
});
Transition.displayName = "Transition";

// Capability columns shown in the grid header.
const CAPABILITIES = [
    { key: "view", label: "View Listing", icon: mdiFileDocumentOutline, color: "#8A94A6" },
    { key: "preview", label: "Preview (Open)", icon: mdiEyeOutline, color: "#5B49D6" },
    { key: "download", label: "Download", icon: mdiDownload, color: "#28B463" },
    { key: "upload", label: "Upload", icon: mdiUpload, color: "#F15A24" },
    { key: "manage", label: "Manage", icon: mdiCog, color: "#E0A200" }
];

// One row per permission level. `value` matches the stored `privacy` string.
const PERMISSION_LEVELS = [
    {
        value: "view",
        name: "View Listing",
        icon: mdiFileDocumentOutline,
        color: "#8A94A6",
        description: "Can view the file and folder listing, but cannot open, preview, download, or upload files.",
        caps: { view: true }
    },
    {
        value: "read",
        name: "Preview",
        icon: mdiEyeOutline,
        color: "#5B49D6",
        description: "Can view the listing and open files to preview them (MS Docs, images, PDFs), but cannot download or upload files.",
        caps: { view: true, preview: true }
    },
    {
        value: "download",
        name: "Download",
        icon: mdiDownload,
        color: "#28B463",
        description: "Can view and preview files and download them, but cannot upload or modify files.",
        caps: { view: true, preview: true, download: true }
    },
    {
        value: "upload",
        name: "Upload",
        icon: mdiUpload,
        color: "#F15A24",
        description: "Can upload files to the shared location and also view and preview files, but cannot download existing files.",
        caps: { view: true, preview: true, upload: true }
    },
    {
        value: "write",
        name: "Full",
        icon: mdiCrown,
        color: "#E0A200",
        description: "Full access to the shared location, including viewing, previewing, downloading, uploading, and managing files.",
        caps: { view: true, preview: true, download: true, upload: true, manage: true }
    }
];

const styles = {
    modalRoot: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
    },
    modal: {
        borderRadius: "8px",
        maxWidth: "920px",
        width: "92%",
        margin: "auto"
    },
    modalHeader: {
        borderBottom: "1px solid #e9edf3",
        padding: "18px 24px",
        position: "relative"
    },
    modalTitle: {
        margin: 0,
        fontSize: "18px",
        fontWeight: 600,
        color: "#1f2a44"
    },
    modalSubTitle: {
        margin: "4px 0 0",
        fontSize: "13px",
        color: "#7a8699"
    },
    modalClose: {
        color: "#8A94A6"
    },
    modalCloseButton: {
        position: "absolute",
        top: "10px",
        right: "10px",
        minWidth: "auto"
    },
    modalBody: {
        padding: "8px 24px 24px"
    },
    scroll: {
        width: "100%",
        overflowX: "auto"
    },
    grid: {
        width: "100%",
        minWidth: "760px",
        borderCollapse: "collapse"
    },
    headCell: {
        textAlign: "center",
        padding: "12px 6px",
        fontSize: "12px",
        fontWeight: 600,
        color: "#1f2a44",
        borderBottom: "1px solid #e9edf3",
        width: "104px",
        verticalAlign: "bottom"
    },
    headLabelCell: {
        borderBottom: "1px solid #e9edf3"
    },
    headIcon: {
        width: "22px",
        height: "22px",
        display: "block",
        margin: "6px auto 0"
    },
    row: {
        borderBottom: "1px solid #f0f3f7"
    },
    levelCell: {
        padding: "16px 12px 16px 0",
        verticalAlign: "top",
        minWidth: "300px"
    },
    levelHeading: {
        display: "flex",
        alignItems: "center"
    },
    levelIcon: {
        width: "26px",
        height: "26px",
        marginRight: "12px",
        flexShrink: 0
    },
    levelName: {
        fontSize: "15px",
        fontWeight: 600,
        color: "#1f2a44"
    },
    levelDesc: {
        margin: "6px 0 0 38px",
        fontSize: "12.5px",
        lineHeight: 1.45,
        color: "#7a8699"
    },
    capCell: {
        textAlign: "center",
        padding: "16px 6px",
        verticalAlign: "middle"
    },
    capIcon: {
        width: "22px",
        height: "22px",
        display: "inline-block"
    }
};

const PermissionInfoModal = class extends React.Component {
    render(){
        const { classes, open } = this.props;
        return (
            <Dialog
                classes={{
                    root: classes.modalRoot,
                    paper: classes.modal
                }}
                open={open}
                TransitionComponent={Transition}
                keepMounted
                onClose={() => this.props.onClose()}
                aria-labelledby="permissionInfoModal-title"
                aria-describedby="permissionInfoModal-description"
            >
                <DialogTitle id="permissionInfoModal-title" disableTypography className={classes.modalHeader}>
                    <Button simple className={classes.modalCloseButton} onClick={() => this.props.onClose()}>
                        <CloseIcon className={classes.modalClose} />
                    </Button>
                    <h4 className={classes.modalTitle}>Permission Levels</h4>
                    <p className={classes.modalSubTitle}>What each access level allows in the shared location.</p>
                </DialogTitle>
                <DialogContent id="permissionInfoModal-description" className={classes.modalBody}>
                    <div className={classes.scroll}>
                        <table className={classes.grid}>
                            <thead>
                                <tr>
                                    <th className={classes.headLabelCell}></th>
                                    {
                                        CAPABILITIES.map((cap) => (
                                            <th key={cap.key} className={classes.headCell}>
                                                {cap.label}
                                                <MaterialIcon path={cap.icon} className={classes.headIcon} color={cap.color} />
                                            </th>
                                        ))
                                    }
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    PERMISSION_LEVELS.map((level) => (
                                        <tr key={level.value} className={classes.row}>
                                            <td className={classes.levelCell}>
                                                <div className={classes.levelHeading}>
                                                    <MaterialIcon path={level.icon} className={classes.levelIcon} color={level.color} />
                                                    <span className={classes.levelName}>{level.name}</span>
                                                </div>
                                                <p className={classes.levelDesc}>{level.description}</p>
                                            </td>
                                            {
                                                CAPABILITIES.map((cap) => (
                                                    <td key={cap.key} className={classes.capCell}>
                                                        <MaterialIcon
                                                            path={cap.icon}
                                                            className={classes.capIcon}
                                                            color={level.caps[cap.key] ? cap.color : "#d6dae1"}
                                                        />
                                                    </td>
                                                ))
                                            }
                                        </tr>
                                    ))
                                }
                            </tbody>
                        </table>
                    </div>
                </DialogContent>
            </Dialog>
        )
    }
}

PermissionInfoModal.defaultProps = {
    open: false,
    onClose: function(){ }
}
PermissionInfoModal.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func
}

export default withStyles(styles)(PermissionInfoModal);
